/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { getPageLinks, type PageNavItem } from './navigationItems';
import { NavItemAnchorMenu } from './NavItemAnchorMenu';
import { usePageItemClick } from './usePageItemClick';

const styles = cssMap({
	centerNav: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		justifySelf: 'center',
		marginBottom: token('space.negative.050'),
	},
	// The hamburger takes over below this width; `NavMenu` shows itself at the
	// same value. 58.5rem (936px), not the 52.5rem `BrandHomeLink` uses: that was
	// measured logged out, where the right cluster ends in a "Log in" button.
	// Logged in it carries the site switcher and avatar, and 936px is where
	// "Send feedback" stopped wrapping and the switcher's label stopped
	// truncating at once.
	hiddenNarrow: {
		'@media (max-width: 58.5rem)': {
			display: 'none',
		},
	},
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		border: 'none',
		borderRadius: token('radius.medium'),
		backgroundColor: 'transparent',
		color: '#B7B9BE',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		// Hover lifts the fill and the label only. The underline is the current-page
		// marker, so drawing it here would tell a visitor they are already on the page
		// they are reaching for.
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			color: '#FFFFFF',
		},
	},
	buttonActive: {
		color: '#FFFFFF',
		// A border rather than a fill so it survives forced-colors mode, which is
		// why ADS Tabs draws its selected line the same way. `currentColor`
		// rather than `color.border.selected`: that token is #1868DB in the light
		// theme, only 3.6:1 against this bar's hardcoded #111213.
		'&::after': {
			content: '""',
			position: 'absolute',
			insetBlockEnd: 0,
			insetInlineStart: token('space.150'),
			insetInlineEnd: token('space.150'),
			height: 0,
			borderBlockEndWidth: token('border.width.selected'),
			borderBlockEndStyle: 'solid',
			borderBlockEndColor: 'currentColor',
		},
	},
});

const NavItemLink = ({ path, routeName, message }: PageNavItem): JSX.Element => {
	const { isActive, onClick } = usePageItemClick(path, routeName);

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- SPA navigation needs native link semantics
		<a
			href={path}
			css={[styles.button, isActive && styles.buttonActive]}
			aria-current={isActive ? 'page' : undefined}
			onClick={onClick}
		>
			<FormattedMessage {...message} />
		</a>
	);
};

type NavButtonsProps = {
	ariaLabel: string;
	isAuthed?: boolean;
	testId?: string;
};

export const NavButtons = ({
	ariaLabel,
	isAuthed = false,
	testId = 'nav-buttons',
}: NavButtonsProps): JSX.Element => {
	const pageLinks = getPageLinks(isAuthed);

	return (
		<nav css={[styles.centerNav, styles.hiddenNarrow]} aria-label={ariaLabel} data-testid={testId}>
			{pageLinks.map((item) =>
				// A link discloses its own page's sections, wherever the visitor
				// currently is, so the list stays one hop away from anywhere. `/cli`
				// carries no sections, so that link stays plain.
				item.anchors && item.anchors.length > 0 ? (
					<NavItemAnchorMenu key={item.routeName} {...item} anchors={item.anchors} />
				) : (
					<NavItemLink key={item.routeName} {...item} />
				),
			)}
		</nav>
	);
};
